"use client";

/**
 * SearchFloatsDisplay - Float search results display
 */

import React, { useState } from "react";
import { Card, Tag } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import { Work, People, Visibility, WorkOff, Description, RocketLaunch, Archive } from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolProgress,
  ToolError,
  ToolEmptyState,
} from "../../Tool";
import { deriveToolState } from "@/lib/sola/types/tools";
import type { ToolDisplayProps } from "@/lib/sola/types/tools";
import { showAllButtonClass } from "../utils";

interface Float {
  id: string | number;
  name?: string;
  title?: string;
  description?: string;
  status?: string;
  candidateCount?: number;
  viewCount?: number;
  createdAt?: string;
  tags?: string[];
}

interface SearchOutput {
  success?: boolean;
  floats?: Float[];
  totalCount?: number;
  message?: string;
}

/** Get avatar color and icon based on float status */
function getFloatStatusStyle(status?: string): { bg: string; icon: React.ReactNode } {
  if (!status) return { bg: colors.action.product, icon: <Work /> };
  switch (status.toLowerCase()) {
    case "active": return { bg: colors.signal.success, icon: <RocketLaunch /> };
    case "draft": return { bg: primitives.neutral[400], icon: <Description /> };
    case "archived": return { bg: primitives.neutral[300], icon: <Archive /> };
    case "sent": return { bg: colors.action.accent, icon: <Work /> };
    default: return { bg: colors.action.product, icon: <Work /> };
  }
}

function FloatCard({ float }: { float: Float }) {
  const statusStyle = getFloatStatusStyle(float.status);
  return (
    <Card
      variant="outlined"
      padding="sm"
      style={{
        borderRadius: radius.md,
        cursor: "pointer",
        transition: "all 0.2s",
      }}
    >
      <div style={{ display: "flex", gap: spacing[4], alignItems: "flex-start" }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: statusStyle.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: colors.text.inverse,
            flexShrink: 0,
          }}
        >
          {statusStyle.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ ...textStyles.body2, fontWeight: 600, display: "block" }}>
            {float.name || float.title || `Float #${float.id}`}
          </span>
          {float.description && (
            <span
              style={{
                ...textStyles.body2,
                color: colors.text.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {float.description}
            </span>
          )}
          <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[2], flexWrap: "wrap" }}>
            {float.candidateCount !== undefined && (
              <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                <People style={{ fontSize: 14, color: colors.text.muted }} />
                <span style={textStyles.caption}>{float.candidateCount} candidates</span>
              </div>
            )}
            {float.viewCount !== undefined && (
              <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
                <Visibility style={{ fontSize: 14, color: colors.text.muted }} />
                <span style={textStyles.caption}>{float.viewCount} views</span>
              </div>
            )}
          </div>
          {float.status && (
            <Tag
              size="sm"
              color={float.status.toLowerCase() === "active" ? "success" : "neutral"}
              style={{ marginTop: spacing[2], height: 20 }}
            >
              {float.status}
            </Tag>
          )}
        </div>
      </div>
    </Card>
  );
}

const MAX_VISIBLE = 8;

export function SearchFloatsDisplay({ toolCall, isStreaming }: ToolDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const toolState = deriveToolState(toolCall);
  const { status, isLoading, isError, error } = toolState;

  const result = toolCall.finalOutput as SearchOutput | undefined;
  const floats = result?.floats || [];
  const totalCount = result?.totalCount || floats.length;
  const visibleFloats = showAll ? floats : floats.slice(0, MAX_VISIBLE);
  const needsTruncation = floats.length > MAX_VISIBLE;

  return (
    <ToolCard status={status} noPadding>
      <ToolHeader
        title={`Search Floats${!isLoading && floats.length > 0 ? ` (${totalCount})` : ""}`}
        toolName={toolCall.toolName}
        status={status}
        isExpanded={isExpanded}
        onToggleExpand={() => setIsExpanded((prev) => !prev)}
        icon={<Work style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen={isExpanded} noPadding>
        {isLoading && (
          <div style={{ padding: spacing[5], textAlign: "center" }}>
            <ToolProgress variant="dots" label="Searching floats..." />
          </div>
        )}

        {isError && error && (
          <div style={{ padding: spacing[4] }}>
            <ToolError error={error} />
          </div>
        )}

        {!isLoading && (
          <>
            <div
              style={{
                paddingLeft: spacing[5],
                paddingRight: spacing[5],
                paddingTop: spacing[3],
                paddingBottom: spacing[3],
                backgroundColor: colors.canvas.card,
                borderBottom: `1px solid ${colors.border.subtle}`,
              }}
            >
              <span style={{ ...textStyles.body2, fontWeight: 500 }}>
                {totalCount} {totalCount === 1 ? "float" : "floats"} found
              </span>
            </div>

            {floats.length > 0 ? (
              <div style={{ padding: spacing[4] }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                    gap: spacing[4],
                  }}
                >
                  {visibleFloats.map((float, i) => (
                    <FloatCard key={float.id || i} float={float} />
                  ))}
                </div>
                {needsTruncation && !showAll && (
                  <button onClick={() => setShowAll(true)} className={showAllButtonClass}>
                    Show all {floats.length} floats
                  </button>
                )}
              </div>
            ) : (
              <ToolEmptyState icon={<WorkOff />} message="No floats found" />
            )}
          </>
        )}
      </ToolBody>
    </ToolCard>
  );
}

export default SearchFloatsDisplay;
